import { useCallback, useRef } from 'react';

/**
 * Sentinel resolved when a promise result is discarded because a newer call
 * has been made since it was issued.
 */
export const STALE = Symbol('stale');

/**
 * Returns a wrap function. Each call bumps an internal generation counter.
 * The returned promise resolves with the original value (or re-throws the
 * original error) only if no newer call has been made by the time it settles;
 * otherwise it resolves with STALE.
 *
 * Use wherever multiple async requests can be in-flight simultaneously and only
 * the latest response should update state (e.g. successive dry-run mutations,
 * search queries, debounced fetches).
 *
 * @example
 * const withLatest = useLatestAsync<MyData>();
 *
 * const result = await withLatest(fetchData(params));
 * if (result === STALE) return;
 * setState(result);
 */
export function useLatestAsync<T>(): (promise: Promise<T>) => Promise<T | typeof STALE> {
  const generationRef = useRef(0);

  return useCallback((promise: Promise<T>): Promise<T | typeof STALE> => {
    const gen = ++generationRef.current;
    return promise.then(
      (value) => (generationRef.current === gen ? value : STALE),
      (error: unknown) => {
        if (generationRef.current === gen) {
          throw error;
        }
        return STALE;
      },
    );
  }, []);
}
